import { useState } from 'react';
import { Button, Dialog, Field, Icon, Input, Panel } from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * MOVING YOUR AUTHENTICATOR TO A NEW PHONE.
 *
 * ── THE OLD ONE KEEPS WORKING THE WHOLE TIME ──────────────────────────────
 *
 * Said on screen in those words, because it is the fact that makes somebody
 * willing to start. The player app's version switches the factor off first, and on
 * a console account that gap is dangerous enough that this flow was built
 * differently: the new secret is held beside the live one until a code from it is
 * proved, so closing this tab halfway costs nothing at all.
 *
 * That is worth stating rather than merely being true. A person who thinks they
 * are about to be locked out of a system that moves money will go and find a
 * Super Admin instead, which is the outcome this screen exists to avoid.
 *
 * ── AND "I HAVE LOST IT" IS DRAWN, NOT HIDDEN ─────────────────────────────
 *
 * There is no self-service route for a lost authenticator, and there must not be:
 * with no confirmed factor a password alone reaches the enrolment step, and the
 * enrolment step mints a verified session. So the control is shown DISABLED with
 * the reason beside it. Somebody in that position needs to be told where to go;
 * showing them nothing leaves them clicking around a screen that has no answer.
 */
export interface AuthenticatorPanelProps {
  confirmedAt: string | null;
  /** An outstanding replacement and its deadline, so a closed tab can be resumed. */
  pendingExpiresAt: string | null;
  onReplaced: (result: { codes: string[] }) => void;
  onStarted: () => void;
  onFailure: (title: string, error: unknown) => void;
}

interface Replacement {
  secret: string;
  uri: string;
  qr: string | null;
  expiresAt: string;
}

export function AuthenticatorPanel({
  confirmedAt,
  pendingExpiresAt,
  onReplaced,
  onStarted,
  onFailure,
}: AuthenticatorPanelProps) {
  /* `prove` asks for the OLD code, `scan` shows the new secret. Two steps, named
     for what the person is doing rather than for a step number. */
  const [stage, setStage] = useState<'idle' | 'prove' | 'scan'>('idle');
  const [oldCode, setOldCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [replacement, setReplacement] = useState<Replacement | null>(null);

  const reset = () => {
    setStage('idle');
    setOldCode('');
    setNewCode('');
    setCodeError(null);
    setReplacement(null);
  };

  const begin = async () => {
    if (busy || oldCode.trim().length < 6) return;
    setBusy(true);
    setCodeError(null);
    try {
      const answer = await api.post<Replacement>('/api/auth/account/two-factor/start', {
        code: oldCode.trim(),
      });
      setReplacement(answer);
      setOldCode('');
      setStage('scan');
      onStarted();
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields?.['code'] !== undefined) {
        setCodeError(error.fields['code']);
      } else {
        onFailure('Could not start setting up a new authenticator', error);
      }
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    if (busy || newCode.trim().length < 6) return;
    setBusy(true);
    setCodeError(null);
    try {
      const answer = await api.post<{ codes: string[] }>(
        '/api/auth/account/two-factor/confirm',
        { code: newCode.trim() },
      );
      reset();
      onReplaced(answer);
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields?.['code'] !== undefined) {
        setCodeError(error.fields['code']);
      } else {
        onFailure('Could not switch to the new authenticator', error);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Authenticator app">
      <div className="stack">
        {confirmedAt === null ? (
          <div className="notice notice-danger">
            <Icon name="warning" />
            <div>
              <strong>No authenticator is set up.</strong> This console requires a second factor,
              so a Super Admin has to set one up before you can finish signing in.
            </div>
          </div>
        ) : (
          <p className="text-subtle">
            Set up{' '}
            {new Date(confirmedAt).toLocaleDateString('en-PH', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            . The console asks for a code from it every time you sign in.
          </p>
        )}

        {pendingExpiresAt !== null && stage === 'idle' && (
          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              You started setting up a new authenticator and have not finished. It stops being
              usable at{' '}
              {new Date(pendingExpiresAt).toLocaleTimeString('en-PH', {
                hour: 'numeric',
                minute: '2-digit',
              })}
              . Your current authenticator still works — start again when you are ready.
            </div>
          </div>
        )}

        <div className="cluster">
          <Button onClick={() => setStage('prove')} disabled={confirmedAt === null}>
            Move to a new phone
          </Button>
          {/* DRAWN, WITH THE REASON. Hiding it leaves somebody who has lost their
              phone clicking around a screen that appears to have no answer. */}
          <Button disabled title="Only a Super Admin can do this">
            I have lost my authenticator
          </Button>
        </div>
        <p className="text-subtle">
          If you have lost it, nobody can switch it off from here — a code from it is exactly
          what this screen would have to ask for. Ask a Super Admin; it is the only thing that
          can reset it.
        </p>
      </div>

      {/* Step one: prove the authenticator being replaced. */}
      <Dialog
        open={stage === 'prove'}
        onClose={() => {
          if (!busy) reset();
        }}
        title="Move to a new phone"
        actions={
          <>
            <Button onClick={reset} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={oldCode.trim().length < 6}
              onClick={() => void begin()}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Your current authenticator keeps working</strong> until you have set the new
              one up and entered a code from it. If you stop halfway, nothing changes.
            </div>
          </div>
          <Field
            label="Code from your current authenticator"
            hint="Or one of your recovery codes."
            {...(codeError !== null ? { error: codeError } : {})}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                autoComplete="one-time-code"
                value={oldCode}
                onChange={(e) => setOldCode(e.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>

      {/* Step two: the new secret, and a code from it. */}
      <Dialog
        open={stage === 'scan' && replacement !== null}
        onClose={() => {
          if (!busy) reset();
        }}
        title="Set up the new authenticator"
        actions={
          <>
            <Button onClick={reset} disabled={busy}>
              Stop — keep my current one
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={newCode.trim().length < 6}
              onClick={() => void finish()}
            >
              Switch to this authenticator
            </Button>
          </>
        }
      >
        <div className="stack">
          {replacement !== null && replacement.qr !== null ? (
            <div className="totp-qr">
              {/*
                AN IMAGE, NOT INLINED MARKUP — the player portal's security screen
                makes the same choice and writes down why. The document comes from
                this platform, but an <img> renders in a context that cannot
                execute anything, so there is no path from a response to a script
                whatever the document turns out to contain.

                WHITE BEHIND IT IN BOTH THEMES, via `.totp-qr`. A QR is black on
                white by specification and carries its own white quiet zone; a dark
                surface under it is the classic reason a code will not scan. This is
                the one surface in the console that does not follow the theme, and
                that is deliberate.

                THE ALT TEXT NAMES THE PICTURE AND NOT ITS CONTENTS. What it
                carries is a shared secret, and a screen reader announcing that in
                a room would be worse than the picture not being there. Anybody who
                cannot use the image has the setup key below it.
              */}
              <img
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(replacement.qr)}`}
                alt="Setup code for this console account, to scan with an authenticator app"
              />
            </div>
          ) : (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                The square could not be drawn for this account — the setup details are too long
                for it. Use the key below instead; it works the same way.
              </div>
            </div>
          )}

          <Field label="Setup key" hint="Type this into your authenticator app if you cannot scan.">
            {({ id, describedBy }) => (
              <Input id={id} aria-describedby={describedBy} readOnly value={replacement?.secret ?? ''} />
            )}
          </Field>

          <Field
            label="Code from the new authenticator"
            hint="Add the account in your app first, then type the six digits it shows."
            {...(codeError !== null ? { error: codeError } : {})}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                autoComplete="one-time-code"
                inputMode="numeric"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
              />
            )}
          </Field>

          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              <p>
                <strong>Your recovery codes will be replaced.</strong> The old ones belong to the
                authenticator you are leaving behind, so a new set is made and shown once.
              </p>
              <p>
                <strong>Every other session ends</strong>, and saved devices will ask for a code
                again. You stay signed in here.
              </p>
            </div>
          </div>
        </div>
      </Dialog>
    </Panel>
  );
}
